import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";

/** Presentation-only split of the existing comma-joined sectionActivityBadge string. */
export const splitActivityBadgeLabels = (sectionActivityBadge) => {
  if (!sectionActivityBadge) {
    return [];
  }

  return String(sectionActivityBadge)
    .split(/,\s*/)
    .map((label) => label.trim())
    .filter(Boolean);
};

const badgeClass = (sectionId, extra = "") =>
  `scr-invoice-row-badge scr-invoice-row-badge--${sectionId || "default"}${extra ? ` ${extra}` : ""}`;

const InvoiceActivityBadgePill = ({ label, sectionId }) => (
  <span className={badgeClass(sectionId)} title={label}>
    {label}
  </span>
);

const BADGE_GAP_PX = 4;

const measureBadgeLayout = (container, measureRoot, labels) => {
  if (!container || !measureRoot || labels.length === 0) {
    return { visible: labels.length, overflow: 0 };
  }

  const availableWidth = container.clientWidth;
  if (availableWidth <= 0) {
    return { visible: labels.length, overflow: 0 };
  }

  const badgeNodes = measureRoot.querySelectorAll("[data-badge-measure]");
  const badgeWidths = Array.from(badgeNodes, (node) => node.offsetWidth);

  const sumBadgeWidths = (count) => {
    let total = 0;
    for (let index = 0; index < count; index += 1) {
      total += badgeWidths[index] || 0;
    }
    if (count > 1) {
      total += BADGE_GAP_PX * (count - 1);
    }
    return total;
  };

  if (sumBadgeWidths(labels.length) <= availableWidth) {
    return { visible: labels.length, overflow: 0 };
  }

  const overflowNode = measureRoot.querySelector("[data-overflow-measure]");

  for (let visible = labels.length - 1; visible >= 0; visible -= 1) {
    const hidden = labels.length - visible;
    if (hidden === 0) {
      continue;
    }

    if (overflowNode) {
      overflowNode.textContent = `+${hidden} more`;
    }

    const overflowWidth = overflowNode?.offsetWidth || 0;
    let total = sumBadgeWidths(visible);
    if (visible > 0) {
      total += BADGE_GAP_PX;
    }
    total += overflowWidth;

    if (total <= availableWidth) {
      return { visible, overflow: hidden };
    }
  }

  if (overflowNode) {
    overflowNode.textContent = `+${labels.length} more`;
  }
  const overflowOnlyWidth = overflowNode?.offsetWidth || 0;
  if (overflowOnlyWidth <= availableWidth) {
    return { visible: 0, overflow: labels.length };
  }

  return { visible: labels.length, overflow: 0 };
};

export const InvoiceActivityBadges = ({ labels, sectionId, rowKey = "" }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [layout, setLayout] = useState(() => ({
    visible: labels.length,
    overflow: 0,
  }));

  const updateLayout = useCallback(() => {
    const next = measureBadgeLayout(containerRef.current, measureRef.current, labels);
    setLayout((current) =>
      current.visible === next.visible && current.overflow === next.overflow ? current : next
    );
  }, [labels]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    let frameId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateLayout);
    });

    observer.observe(container);
    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [updateLayout]);

  if (labels.length === 0) {
    return null;
  }

  const visibleLabels = labels.slice(0, layout.visible);
  const hiddenLabels = labels.slice(layout.visible);
  const popoverId = `scr-invoice-badge-overflow-${rowKey || sectionId || "row"}`;

  return (
    <div className="scr-invoice-badges" ref={containerRef}>
      <div className="scr-invoice-badges__visible">
        {visibleLabels.map((label, index) => (
          <InvoiceActivityBadgePill key={`${label}-${index}`} label={label} sectionId={sectionId} />
        ))}
        {layout.overflow > 0 ? (
          <OverlayTrigger
            trigger={["hover", "focus", "click"]}
            placement="top"
            rootClose
            overlay={
              <Popover id={popoverId} className="scr-invoice-badge-popover-root">
                <Popover.Body className="scr-invoice-badge-popover">
                  <div className="scr-invoice-badge-popover__badges">
                    {hiddenLabels.map((label, index) => (
                      <InvoiceActivityBadgePill
                        key={`hidden-${label}-${index}`}
                        label={label}
                        sectionId={sectionId}
                      />
                    ))}
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <span
              className={badgeClass(sectionId, "scr-invoice-row-badge--overflow")}
              role="button"
              tabIndex={0}
              aria-label={`${layout.overflow} more activity badges`}
            >
              +{layout.overflow} more
            </span>
          </OverlayTrigger>
        ) : null}
      </div>
      <div ref={measureRef} className="scr-invoice-badges__measure" aria-hidden="true">
        {labels.map((label, index) => (
          <span key={`measure-${index}`} data-badge-measure className={badgeClass(sectionId)}>
            {label}
          </span>
        ))}
        <span data-overflow-measure className={badgeClass(sectionId, "scr-invoice-row-badge--overflow")}>
          +9 more
        </span>
      </div>
    </div>
  );
};

export const PrintInvoiceActivityBadges = ({ sectionActivityBadge, sectionId }) => {
  const labels = splitActivityBadgeLabels(sectionActivityBadge);
  if (labels.length === 0) {
    return null;
  }

  return (
    <span className="scr-v2-print-invoice-badges">
      {labels.map((label, index) => (
        <span
          key={`${label}-${index}`}
          className={`scr-v2-print-invoice-badge scr-v2-print-invoice-badge--${sectionId || "default"}`}
        >
          {label}
        </span>
      ))}
    </span>
  );
};
